import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  type AutocompleteInputChangeReason,
  Avatar,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { Building2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { GetMlsTeamsQuery } from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';

const SEARCH_DEBOUNCE_MS = 275;

// Query teams directly from mls_teams (cross-tenant view)
const GET_MLS_TEAMS = gql(/* GraphQL */ `
  query GetMlsTeams($selectedIds: [uuid!]!, $hasSelectedIds: Boolean!) {
    # Get selected teams (only when IDs are provided)
    selected_teams: mls_teams(where: { id: { _in: $selectedIds } })
      @include(if: $hasSelectedIds) {
      id
      name
      logo {
        url
      }
    }
    # Get initial list of teams (alphabetically)
    initial_teams: mls_teams(order_by: [{ name: asc }], limit: 20) {
      id
      name
      logo {
        url
      }
    }
  }
`);

// Search teams using ilike on name field
const SEARCH_MLS_TEAMS = gql(/* GraphQL */ `
  query SearchMlsTeams($search: String!) {
    mls_teams(
      where: { name: { _ilike: $search } }
      order_by: [{ name: asc }]
      limit: 20
    ) {
      id
      name
      logo {
        url
      }
    }
  }
`);

// Use the generated type from the query
type RawTeam = GetMlsTeamsQuery['initial_teams'][number];
type TeamOption = RawTeam & { id: string; allIds: string[] };

// Type guard to filter out teams without required id
const hasTeamId = (team: RawTeam): team is RawTeam & { id: string } =>
  team.id != null;

// Dedupe teams by name — merge all IDs for the same name into one option
const dedupeTeams = (teams: (RawTeam & { id: string })[]): TeamOption[] => {
  const byName = new Map<string, TeamOption>();
  for (const team of teams) {
    const key = (team.name ?? '').toLowerCase();
    const existing = byName.get(key);
    if (existing) {
      if (!existing.allIds.includes(team.id)) {
        existing.allIds.push(team.id);
      }
    } else {
      byName.set(key, { ...team, allIds: [team.id] });
    }
  }
  return [...byName.values()];
};

interface MlsTeamMultiSelectProps {
  onClose?: () => void;
  onChange: (value: string[]) => void;
  defaultValue: string[];
}

const MlsTeamMultiSelect = ({
  onChange,
  onClose,
  defaultValue,
}: MlsTeamMultiSelectProps) => {
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<TeamOption[]>([]);
  const [options, setOptions] = useState<TeamOption[]>([]);

  const hasSelectedIds = defaultValue.length > 0;
  const { data, loading: defaultLoading } = useQuery(GET_MLS_TEAMS, {
    variables: {
      selectedIds: hasSelectedIds ? defaultValue : [],
      hasSelectedIds,
    },
  });

  const [searchTeams, { loading: searchLoading }] = useLazyQuery(
    SEARCH_MLS_TEAMS,
    { notifyOnNetworkStatusChange: true },
  );

  // Update options from query results — dedupe by name across tenants
  const updateOptions = useCallback(
    (
      searchResults?: (RawTeam & { id: string })[],
      initialTeams?: (RawTeam & { id: string })[],
    ) => {
      const combined = [...(searchResults ?? []), ...(initialTeams ?? [])];
      setOptions(dedupeTeams(combined));
    },
    [],
  );

  // Initialize options and selected from data
  useEffect(() => {
    if (data && options.length === 0 && !inputValue) {
      const rawSelected = (data.selected_teams ?? []).filter(hasTeamId);
      const rawInitial = (data.initial_teams ?? []).filter(hasTeamId);
      const allDeduped = dedupeTeams([...rawSelected, ...rawInitial]);
      // Mark options that contain any of the currently selected IDs
      const selectedIdSet = new Set(defaultValue);
      const selected = allDeduped.filter(opt =>
        opt.allIds.some(id => selectedIdSet.has(id)),
      );
      setSelectedOptions(selected);
      setOptions(allDeduped);
    }
  }, [data, options.length, inputValue, defaultValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        const { data: searchData } = await searchTeams({
          variables: {
            search: `%${searchValue}%`,
          },
        });
        if (searchData?.mls_teams) {
          setOptions(dedupeTeams(searchData.mls_teams.filter(hasTeamId)));
        } else {
          setOptions([]);
        }
      }, SEARCH_DEBOUNCE_MS),
    [searchTeams],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue) {
        debouncedSearch(searchValue);
      } else {
        // Reset to initial options
        updateOptions(
          (data?.selected_teams ?? []).filter(hasTeamId),
          (data?.initial_teams ?? []).filter(hasTeamId),
        );
      }
    },
    [data?.initial_teams, data?.selected_teams, debouncedSearch, updateOptions],
  );

  const handleToggleOptions = useCallback(
    (_event: SyntheticEvent, newValue: TeamOption[]) => {
      setSelectedOptions(newValue);
      // Emit all IDs for selected options (covers cross-tenant duplicates)
      onChange(newValue.flatMap(v => v.allIds));
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason !== 'input') {
        return;
      }
      setInputValue(newValue);
      handleSearch(newValue);
    },
    [handleSearch],
  );

  const loading = defaultLoading || searchLoading;

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      disableClearable
      size="small"
      clearOnBlur={false}
      value={selectedOptions}
      loading={loading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={options}
      getOptionLabel={option => option.name ?? ''}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, value) =>
        (option.name ?? '').toLowerCase() === (value.name ?? '').toLowerCase()
      }
      onChange={handleToggleOptions}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const logoUrl = option.logo?.url;
        return (
          <ListItem {...props} key={option.id} dense>
            <ListItemIcon sx={{ minWidth: 0, alignItems: 'center' }}>
              <Checkbox
                edge="start"
                checked={selected}
                tabIndex={-1}
                disableRipple
              />
              {/* Neutralise ListItemAvatar's default min-width:56 / margin-top:8
                  so the logo stays centered next to the name. */}
              <ListItemAvatar sx={{ minWidth: 0, my: 0, mr: 1 }}>
                <Avatar
                  src={logoUrl ?? undefined}
                  variant="rounded"
                  sx={{ width: 32, height: 32 }}
                >
                  {option.name?.[0]}
                </Avatar>
              </ListItemAvatar>
            </ListItemIcon>
            <ListItemText primary={option.name} />
          </ListItem>
        );
      }}
      noOptionsText={t('No options')}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

interface MlsTeamFilterProps extends QuickFilterProps {
  // When true, the filter target is the `id` field itself (e.g. path ends with 'id')
  // so the where clause is written as `{ _in: [...] }` instead of `{ id: { _in: [...] } }`.
  idField?: boolean;
}

export const MlsTeamFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
  idField = false,
}: MlsTeamFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();

  const defaultValue = useMemo(
    () =>
      idField
        ? getValueFromPath(path, where)?._in ?? []
        : getValueFromPath(path, where)?.id?._in ?? [],
    [idField, getValueFromPath, path, where],
  );

  const handleChange = useCallback(
    (value: string[]) => {
      let newWhere = deleteWhereClause(where, path, true);

      if (value.length > 0) {
        const addPath = idField ? [...path, '_in'] : [...path, 'id', '_in'];
        newWhere = addWhereClause(newWhere, addPath, value);
      }
      setFiltersParams(newWhere);
    },
    [idField, deleteWhereClause, where, path, setFiltersParams, addWhereClause],
  );

  const handleDelete =
    defaultValue.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<Building2Icon size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <MlsTeamMultiSelect
          onClose={handleClose}
          onChange={handleChange}
          defaultValue={defaultValue}
        />
      )}
    />
  );
};
